import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';

/* ─── SVG Icons ──────────────────────────────────────────────────── */
const Icon = {
  tracker: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
      <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
    </svg>
  ),
  ai: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L13.09 8.26L19 6L15.74 11L22 12L15.74 13L19 18L13.09 15.74L12 22L10.91 15.74L5 18L8.26 13L2 12L8.26 11L5 6L10.91 8.26L12 2Z"/>
    </svg>
  ),
  contacts: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
    </svg>
  ),
  document: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
    </svg>
  ),
  templates: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  ),
  chrome: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/>
      <line x1="21.17" y1="8" x2="12" y2="8"/><line x1="3.95" y1="6.06" x2="8.54" y2="14"/>
      <line x1="10.88" y1="21.94" x2="15.46" y2="14"/>
    </svg>
  ),
  check: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  arrow: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
    </svg>
  ),
};

const FEATURES = [
  { icon: Icon.tracker, title: 'Application Tracker', desc: 'Visualise your pipeline in a list or kanban board. Track status, salary, deadlines, and excitement rating in one view.', color: '#6366f1', bg: '#eef2ff' },
  { icon: Icon.ai,      title: 'AI Email Assistant', desc: 'Generate tailored follow-ups, cold outreach, and thank-you emails in seconds. Upload your resume for personalised content.', color: '#8b5cf6', bg: '#f5f3ff' },
  { icon: Icon.contacts,title: 'Contacts Manager', desc: 'Log recruiters and hiring managers. Track outreach status, link contacts to jobs, and never miss a follow-up.', color: '#0ea5e9', bg: '#f0f9ff' },
  { icon: Icon.document, title: 'Document Storage', desc: 'Upload resumes, cover letters, and portfolios per application. Rename, preview, and download anytime.', color: '#f59e0b', bg: '#fffbeb' },
  { icon: Icon.templates,title: 'Email Templates', desc: 'Build a library of reusable email templates. Fill smart placeholders and send polished messages instantly.', color: '#10b981', bg: '#f0fdf4' },
  { icon: Icon.chrome,   title: 'Chrome Extension', desc: 'Save jobs from LinkedIn, Indeed, and Greenhouse with one click — company, role, and description captured automatically.', color: '#64748b', bg: '#f8fafc', soon: true },
];

const STATS = [
  { value: '11', label: 'Application statuses' },
  { value: 'AI', label: 'Powered emails' },
  { value: '∞', label: 'Jobs you can track' },
  { value: '0', label: 'Spreadsheets needed' },
];

/* ─── Animated counter ───────────────────────────────────────────── */
function Counter({ target, suffix = '' }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const num = parseInt(target);
    if (isNaN(num)) { setVal(target); return; }
    let start = 0;
    const step = Math.ceil(num / 30);
    const id = setInterval(() => {
      start += step;
      if (start >= num) { setVal(num); clearInterval(id); }
      else setVal(start);
    }, 40);
    return () => clearInterval(id);
  }, [target]);
  return <>{val}{suffix}</>;
}

export default function HomePage() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const isLoggedIn = !!localStorage.getItem('applyai_token');

  return (
    <div className="hp">

      {/* ── Navbar ─────────────────────────────────────── */}
      <header className="hp-nav">
        <div className="hp-nav-inner">
          <Link to="/" className="hp-logo">
            <span className="logo-badge" style={{ fontSize: '0.72rem' }}>AI</span>
            <span className="hp-logo-name">ApplyAI</span>
          </Link>

          <nav className="hp-nav-links">
            <a href="#features">Features</a>
            <a href="#how">How it works</a>
          </nav>

          <div className="hp-nav-cta">
            {isLoggedIn ? (
              <button className="hp-btn-primary" onClick={() => navigate('/dashboard')}>
                Open Dashboard {Icon.arrow}
              </button>
            ) : (
              <>
                <Link to="/login" className="hp-btn-ghost">Sign in</Link>
                <Link to="/signup" className="hp-btn-primary">Get started free</Link>
              </>
            )}
          </div>

          <button className="hp-hamburger" onClick={() => setMenuOpen(o => !o)}>
            {menuOpen
              ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
            }
          </button>
        </div>
        {menuOpen && (
          <div className="hp-mobile-menu">
            <a href="#features" onClick={() => setMenuOpen(false)}>Features</a>
            <a href="#how" onClick={() => setMenuOpen(false)}>How it works</a>
            {isLoggedIn ? (
              <button className="hp-btn-primary" onClick={() => { setMenuOpen(false); navigate('/dashboard'); }}>Open Dashboard</button>
            ) : (
              <>
                <Link to="/login" onClick={() => setMenuOpen(false)}>Sign in</Link>
                <Link to="/signup" className="hp-btn-primary" onClick={() => setMenuOpen(false)}>Get started free</Link>
              </>
            )}
          </div>
        )}
      </header>

      {/* ── Hero ───────────────────────────────────────── */}
      <section className="hp-hero">
        <div className="hp-hero-inner">
          <div className="hp-hero-badge">
            <span className="hp-badge-dot" />
            Intelligent job search tracker
          </div>

          <h1 className="hp-hero-h1">
            Land your next role<br />
            <span className="hp-gradient-text">faster than ever before</span>
          </h1>

          <p className="hp-hero-sub">
            Track every application, write AI-powered emails, and manage recruiter relationships — all without touching a spreadsheet.
          </p>

          <div className="hp-hero-actions">
            {isLoggedIn ? (
              <button className="hp-cta-btn" onClick={() => navigate('/dashboard')}>
                Open Dashboard
                <span className="hp-cta-arrow">{Icon.arrow}</span>
              </button>
            ) : (
              <Link to="/signup" className="hp-cta-btn">
                Start for free
                <span className="hp-cta-arrow">{Icon.arrow}</span>
              </Link>
            )}
            <a href="#features" className="hp-outline-btn">
              Explore features
            </a>
          </div>

          <div className="hp-hero-social">
            <div className="hp-checks">
              {['No credit card required', 'Free to use', 'Set up in 60 seconds'].map(t => (
                <span key={t} className="hp-check-item">
                  <span className="hp-check-icon">{Icon.check}</span>{t}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* App mockup */}
        <div className="hp-mockup-wrap">
          <div className="hp-mockup">
            <div className="hp-mockup-chrome">
              <div className="hp-chrome-dots">
                <span /><span /><span />
              </div>
              <div className="hp-chrome-url">applyai.app/dashboard</div>
            </div>
            <div className="hp-mockup-body">
              <div className="hp-mock-sidebar">
                <div className="hp-mock-logo">
                  <span style={{ fontSize: '0.55rem', fontWeight: 800, color: '#6366f1' }}>AI</span>
                </div>
                {['⊞','👥','✉','✦'].map((ic, i) => (
                  <div key={i} className={`hp-mock-nav ${i === 0 ? 'active' : ''}`}>{ic}</div>
                ))}
              </div>
              <div className="hp-mock-main">
                <div className="hp-mock-header">
                  <span className="hp-mock-title">Job Tracker</span>
                  <span className="hp-mock-add">+ Add Job</span>
                </div>
                <div className="hp-mock-pills">
                  {['All','Saved','Applied','Screen','Interviewing'].map((p, i) => (
                    <span key={p} className={`hp-mock-pill ${i === 0 ? 'active' : ''}`}>{p}</span>
                  ))}
                </div>
                <div className="hp-mock-rows">
                  {[
                    { co: 'Stripe', role: 'SWE Intern', s: 'Interviewing', sc: '#fef3c7', tc: '#92400e' },
                    { co: 'Figma',  role: 'Frontend Eng', s: 'Applied', sc: '#e0e7ff', tc: '#4338ca' },
                    { co: 'Linear', role: 'Product Design', s: 'Saved', sc: '#f1f5f9', tc: '#475569' },
                    { co: 'Vercel', role: 'Dev Advocate', s: 'Screen', sc: '#cffafe', tc: '#0e7490' },
                    { co: 'Notion', role: 'Full Stack Eng', s: 'Offer', sc: '#dcfce7', tc: '#166534' },
                  ].map((r, i) => (
                    <div key={i} className="hp-mock-row">
                      <span className="hp-mock-co">{r.co}</span>
                      <span className="hp-mock-role">{r.role}</span>
                      <span className="hp-mock-badge" style={{ background: r.sc, color: r.tc }}>{r.s}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          {/* Floating cards */}
          <div className="hp-float-card hp-float-1">
            <span className="hp-float-icon" style={{ color: '#10b981' }}>✦</span>
            <div>
              <div className="hp-float-title">AI Email drafted</div>
              <div className="hp-float-sub">Follow-up to Stripe recruiter</div>
            </div>
          </div>
          <div className="hp-float-card hp-float-2">
            <span className="hp-float-icon" style={{ color: '#f59e0b' }}>◉</span>
            <div>
              <div className="hp-float-title">Interview scheduled</div>
              <div className="hp-float-sub">Figma · Tomorrow 2 PM</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats bar ──────────────────────────────────── */}
      <section className="hp-stats">
        <div className="hp-stats-inner">
          {STATS.map(s => (
            <div key={s.label} className="hp-stat">
              <div className="hp-stat-val"><Counter target={s.value} /></div>
              <div className="hp-stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Features ───────────────────────────────────── */}
      <section id="features" className="hp-features">
        <div className="hp-section">
          <div className="hp-section-eyebrow">Features</div>
          <h2 className="hp-section-h2">Built for every stage of your search</h2>
          <p className="hp-section-sub">From the first saved listing to signing an offer — ApplyAI keeps you in control.</p>
          <div className="hp-features-grid">
            {FEATURES.map(f => (
              <div key={f.title} className="hp-feat-card">
                {f.soon && <span className="hp-soon-badge">Coming soon</span>}
                <div className="hp-feat-icon" style={{ background: f.bg, color: f.color }}>
                  {f.icon}
                </div>
                <h3 className="hp-feat-title">{f.title}</h3>
                <p className="hp-feat-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ───────────────────────────────── */}
      <section id="how" className="hp-how">
        <div className="hp-section">
          <div className="hp-section-eyebrow">How it works</div>
          <h2 className="hp-section-h2">From listing to offer in three steps</h2>
          <div className="hp-steps">
            {[
              { n: '01', title: 'Save a job', desc: 'Paste a job URL or use the Chrome extension to capture the role, company, and description instantly.' },
              { n: '02', title: 'Track & communicate', desc: 'Update status as you progress. Let AI write your emails while you focus on interview prep.' },
              { n: '03', title: 'Land the role', desc: 'Use data from your tracker to follow up at the right time, negotiate confidently, and accept the best offer.' },
            ].map((s, i) => (
              <div key={s.n} className="hp-step">
                <div className="hp-step-n">{s.n}</div>
                <div className="hp-step-connector" style={{ opacity: i < 2 ? 1 : 0 }} />
                <h3 className="hp-step-title">{s.title}</h3>
                <p className="hp-step-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ──────────────────────────────────── */}
      <section className="hp-final-cta">
        <div className="hp-section" style={{ textAlign: 'center' }}>
          <div className="hp-cta-badge">Ready to start?</div>
          <h2 className="hp-cta-h2">Take control of your job search today</h2>
          <p className="hp-cta-sub">Free to use. No spreadsheets. No chaos.</p>
          {isLoggedIn ? (
            <button className="hp-cta-btn hp-cta-btn-lg" onClick={() => navigate('/dashboard')}>
              Open Dashboard {Icon.arrow}
            </button>
          ) : (
            <Link to="/signup" className="hp-cta-btn hp-cta-btn-lg">
              Create your free account {Icon.arrow}
            </Link>
          )}
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────── */}
      <footer className="hp-footer">
        <div className="hp-nav-inner" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <Link to="/" className="hp-logo" style={{ textDecoration: 'none' }}>
            <span className="logo-badge" style={{ fontSize: '0.72rem' }}>AI</span>
            <span className="hp-logo-name" style={{ color: '#94a3b8' }}>ApplyAI</span>
          </Link>
          <div style={{ display: 'flex', gap: '24px' }}>
            <Link to="/login" style={{ color: '#64748b', fontSize: '0.85rem', textDecoration: 'none' }}>Sign in</Link>
            <Link to="/signup" style={{ color: '#64748b', fontSize: '0.85rem', textDecoration: 'none' }}>Sign up</Link>
            <a href="#features" style={{ color: '#64748b', fontSize: '0.85rem', textDecoration: 'none' }}>Features</a>
          </div>
          <p style={{ color: '#475569', fontSize: '0.82rem', margin: 0 }}>
            © {new Date().getFullYear()} ApplyAI · Track smarter, land faster.
          </p>
        </div>
      </footer>
    </div>
  );
}
